/**
 * Opportunity Card Grid — visual, narrative view.
 */
import React from "react";
import { ArrowRight, Calendar, Clock, Layers, Wallet } from "lucide-react";
import {
  fmtAud,
  SeverityChip,
} from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

const STATUS_TONE = {
  new: "bg-tralenex-50 text-tralenex-700",
  endorsed: "bg-gold-300/10 text-gold-600",
  "in-review": "bg-inset text-ink-secondary",
  "in-flight": "bg-opportunity/10 text-opportunity",
  live: "bg-risk-low/10 text-risk-low",
};

export function OpportunityCardGrid({ items, onSelect }) {
  if (items.length === 0) {
    return (
      <div className="surface-card shadow-e1 p-10 text-center text-ink-secondary" data-testid="radar-cards-empty">
        No opportunities match the current filters.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="radar-cards">
      {items.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onSelect(o)}
          className="group surface-card text-left p-4 transition-all hover:border-tralenex-300 hover:shadow-e2 focus:outline-none focus:ring-2 focus:ring-tralenex-200"
          data-testid={`radar-card-${o.id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <span className="text-meta font-mono text-ink-disabled tabular-nums">{o.id}</span>
            <span
              className={cn(
                "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase",
                STATUS_TONE[o.status] || "bg-inset text-ink-secondary",
              )}
            >
              {o.status}
            </span>
          </div>
          <h3 className="mt-2 text-h3 text-ink-primary line-clamp-2 group-hover:text-tralenex-900 transition-colors">
            {o.title}
          </h3>
          <p className="mt-1 text-meta text-ink-secondary truncate">
            {o.department} · {o.businessUnit}
          </p>

          {/* Score strip */}
          <div className="mt-3 grid grid-cols-4 gap-2 text-center">
            <ScoreCell label="ROI" value={o.roiScore} tone="tralenex" />
            <ScoreCell label="Risk" value={o.riskScore} tone={o.risk === "high" ? "high" : o.risk === "med" ? "med" : "low"} />
            <ScoreCell label="Effort" value={o.effortScore} tone="neutral" />
            <ScoreCell label="Priority" value={o.priorityScore} tone="gold" />
          </div>

          {/* Meta strip */}
          <ul className="mt-3 space-y-1.5 text-meta">
            <MetaRow icon={Wallet} text={`${fmtAud(o.estimatedValueAud)} est. annual value`} />
            <MetaRow icon={Clock} text={`${o.paybackMonths} mo payback · ${o.timelineWeeks} wk delivery`} />
            <MetaRow icon={Layers} text={o.recommendedApproach} />
            <MetaRow
              icon={Calendar}
              text={
                <>
                  Compliance impact <SeverityChip severity={o.complianceImpact} />
                </>
              }
            />
          </ul>

          <p className="mt-3 text-meta font-semibold text-tralenex-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Open detail <ArrowRight size={12} />
          </p>
        </button>
      ))}
    </div>
  );
}

const SCORE_TONE = {
  tralenex: "text-tralenex-700",
  low: "text-risk-low",
  med: "text-risk-med",
  high: "text-risk-high",
  neutral: "text-ink-primary",
  gold: "text-gold-600",
};

function ScoreCell({ label, value, tone }) {
  return (
    <div className="rounded-md bg-inset/60 px-1.5 py-1.5">
      <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-ink-secondary">{label}</p>
      <p className={cn("mt-0.5 text-base font-semibold tabular-nums leading-none", SCORE_TONE[tone])}>
        {value}
      </p>
    </div>
  );
}

function MetaRow({ icon: Icon, text }) {
  return (
    <li className="flex items-center gap-2 text-ink-secondary">
      <Icon size={12} className="shrink-0" />
      <span className="truncate flex items-center gap-1.5">{text}</span>
    </li>
  );
}
